<script setup lang="ts">
import { computed, inject, ref, watch, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery } from '@tanstack/vue-query';
import rutHelpers from 'rut-helpers';
import {
  patientsApi, type XlsxPatients, type PatientStatusBucket,
} from '@/api/patients';
import { type SerializedUser } from '@/api/users';
import { useTableFilter } from '@/composables/useTableFilter';
import { usePermissions } from '@/composables/usePermissions';
import { useFileDownload } from '@/composables/useFileDownload';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { useAnalytics, AnalyticsEvents } from '@/composables/useAnalytics';
import { useGroupFilterStore } from '@/stores/group-filter';
import { useServiceCategoryFilterStore } from '@/stores/service-category-filter';
import { storeToRefs } from 'pinia';
import {
  bucketOf, statusChipColor, statusChipLabel, statusChipTooltip, formatShortDate,
} from '@/utils/patient-status';
import GroupFilterSelect from '@/components/group-filter-select.vue';
import ServiceCategoryFilterSelect from '@/components/service-category-filter-select.vue';
import ListToolbar from '@/components/list-toolbar.vue';
import PatientDiscardForm from './patient-discard-form.vue';
import PatientUndiscardForm from './patient-undiscard-form.vue';

type Scope = PatientStatusBucket;
type Header = { title: string; value: string; sortable?: boolean };

const { t } = useI18n();
const { downloadFile } = useFileDownload();
const { hasPermission } = usePermissions();
const { trackPatientEvent } = useAnalytics();
const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 10 });

const currentUser = inject<Ref<SerializedUser>>('currentUser');
const usesIdCode = !!currentUser?.value?.companyUsesIdCode;
const serviceCategoriesEnabled = !!currentUser?.value?.companyPatientServiceCategoriesEnabled;
// Activos / Inactivos / Archivados currency is a care-planning feature; other companies keep
// the legacy two-tab list untouched.
const statusEnabled = !!currentUser?.value?.companyCarePlanningEnabled;

const { groupIds: groupIdIn } = storeToRefs(useGroupFilterStore());
const {
  categoryIds: serviceCategoryIdIn,
  uncategorized,
} = storeToRefs(useServiceCategoryFilterStore());

// One request for kept patients; Activos/Inactivos are split client-side by the computed
// bucket so the tab counts come for free.
const { data: keptPatients, refetch: refetchKeptPatients, isPending, isFetching } = useQuery({
  queryKey: ['activePatients', groupIdIn, serviceCategoryIdIn, uncategorized],
  queryFn: () => patientsApi.indexForList('active', groupIdIn.value,
    serviceCategoryIdIn.value, uncategorized.value),
});

const { data: discardedPatients, refetch: refetchDiscardedPatients } = useQuery({
  queryKey: ['discardedPatients', groupIdIn, serviceCategoryIdIn, uncategorized],
  queryFn: () => patientsApi.indexForList('discarded', groupIdIn.value,
    serviceCategoryIdIn.value, uncategorized.value),
});

const activePatients = computed(() => {
  const kept = keptPatients.value ?? [];

  return statusEnabled ? kept.filter((p) => bucketOf(p.status) === 'active') : kept;
});
const inactivePatients = computed(() => (
  statusEnabled ? (keptPatients.value ?? []).filter((p) => bucketOf(p.status) === 'inactive') : []
));

// Deep-link a tab via ?tab= (the Planificación "Revisar" link lands on Inactivos).
function initialScope(): Scope {
  const requested = new URLSearchParams(window.location.search).get('tab');
  const allowed: Scope[] = statusEnabled ? ['active', 'inactive', 'discarded'] : ['active', 'discarded'];

  return allowed.includes(requested as Scope) ? (requested as Scope) : 'active';
}

const scope = ref<Scope>(initialScope());

const patients = computed(() => {
  if (scope.value === 'discarded') return discardedPatients.value ?? [];
  if (scope.value === 'inactive') return inactivePatients.value;

  return activePatients.value;
});

// Inactivos reads oldest-first; other tabs keep the server order (name / id code).
type SortItem = { key: string; order: 'asc' | 'desc' };
const INACTIVE_SORT: SortItem[] = [{ key: 'status.inactiveDays', order: 'desc' }];
const sortBy = ref<SortItem[]>(scope.value === 'inactive' ? [...INACTIVE_SORT] : []);
watch(scope, (value) => {
  sortBy.value = value === 'inactive' ? [...INACTIVE_SORT] : [];
});

function tabLabel(bucket: Scope, count: number): string {
  const label = t(`patientStatus.tabs.${bucket}`);

  return statusEnabled ? `${label} (${count})` : label;
}

const hasActions = computed(() =>
  hasPermission.value('patients', 'show') ||
  hasPermission.value('patients', 'update') ||
  !hasPermission.value('patients', 'destroy'),
);

function identityHeaders(): Header[] {
  const base: Header[] = [
    { title: t('patient.firstName'), value: 'firstName', sortable: true },
    { title: t('patient.lastName'), value: 'lastName', sortable: true },
    { title: t('patient.dni'), value: 'dni' },
  ];
  if (usesIdCode) base.unshift({ title: t('patient.idCode'), value: 'idCode', sortable: true });
  if (serviceCategoriesEnabled) {
    base.push({ title: t('patientForm.serviceCategory'), value: 'serviceCategoryName', sortable: true });
  }

  return base;
}

// Archivados (care planning): why, when and by whom.
function discardedHeaders(): Header[] {
  return [
    { title: t('patientStatus.columns.discardReason'), value: 'humanDiscardReason', sortable: true },
    { title: t('patientStatus.columns.endsOn'), value: 'endsOn', sortable: true },
    { title: t('patientStatus.columns.discardedAt'), value: 'discardedAt', sortable: true },
    { title: t('patientStatus.columns.discardedBy'), value: 'discardedByName', sortable: true },
  ];
}

// Activos / Inactivos (care planning): one Estado column; the chip carries the detail
// (inactive age as text, active reason as tooltip). Phone stays under the name.
function statusHeaders(): Header[] {
  return [
    { title: t('patientStatus.columns.status'), value: 'status.inactiveDays', sortable: true },
    { title: 'Apoderado', value: 'representative.fullName', sortable: true },
  ];
}

function legacyHeaders(): Header[] {
  return [
    { title: 'Apoderado', value: 'representative.fullName', sortable: true },
    { title: 'Teléfono Apoderado', value: 'representative.phoneNumber' },
  ];
}

function scopeHeaders(): Header[] {
  if (!statusEnabled) return legacyHeaders();

  return scope.value === 'discarded' ? discardedHeaders() : statusHeaders();
}

const headers = computed<Header[]>(() => {
  const result = [...identityHeaders(), ...scopeHeaders()];
  if (hasActions.value) result.push({ title: 'Acciones', value: 'actions' });

  return result;
});

const actionSuccess = ref(false);

const search = ref('');

const activeFilterCount = computed(() => (groupIdIn.value.length > 0 ? 1 : 0) +
  (serviceCategoryIdIn.value.length > 0 || uncategorized.value ? 1 : 0));

const customFilter = useTableFilter([
  'firstName',
  'lastName',
  'dni',
  'representative.fullName',
  'representative.phoneNumber',
  'idCode',
  'serviceCategoryName',
  'serviceSubcategoryName',
  'humanDiscardReason',
], { rutFields: ['dni'] });

// A patient moves tab when its requirement ends; tell the searcher where else the
// query matches so the move is visible instead of surprising.
const otherKeptTab = computed<Scope | null>(() => {
  if (!statusEnabled || scope.value === 'discarded') return null;

  return scope.value === 'active' ? 'inactive' : 'active';
});
const otherTabMatches = computed(() => {
  if (!otherKeptTab.value || !search.value) return 0;
  const list = otherKeptTab.value === 'active' ? activePatients.value : inactivePatients.value;

  return list.filter((p) => customFilter('', search.value, { raw: p })).length;
});

const xlsxDownloadSnackbarAlert = ref(false);
function downloadPatientsXlsx() {
  trackPatientEvent(AnalyticsEvents.patients.fileDownloaded, {
    format: 'xlsx',
    scope: scope.value,
  });
  xlsxDownloadSnackbarAlert.value = true;

  const serverScope = scope.value === 'discarded' ? 'discarded' : 'active';
  const status = statusEnabled && scope.value !== 'discarded' ? scope.value : undefined;
  patientsApi.indexAsXlsx(serverScope, {
    groupIds: groupIdIn.value,
    serviceCategoryIdIn: serviceCategoryIdIn.value,
    uncategorized: uncategorized.value,
    status,
  }).then((response: XlsxPatients) => {
    downloadFile(response);
  });
}

function refetchAll() {
  refetchKeptPatients();
  refetchDiscardedPatients();
}

</script>
<template>
  <v-card class="w-100">
    <v-data-table
      v-bind="tableProps"
      v-model:sort-by="sortBy"
      :headers="headers"
      :header-props="{ class: 'font-weight-bold' }"
      :items="patients"
      :search="search"
      :custom-filter="customFilter"
      :loading="isPending"
      loading-text="Cargando los pacientes..."
    >
      <template #top>
        <list-toolbar
          v-model:search="search"
          title="Pacientes"
          search-label="Buscar"
          :active-filter-count="activeFilterCount"
        >
          <template #actions>
            <v-btn
              v-if="statusEnabled || scope === 'active'"
              color="primary"
              prepend-icon="mdi-microsoft-excel"
              variant="outlined"
              :disabled="isFetching || xlsxDownloadSnackbarAlert"
              @click="downloadPatientsXlsx"
            >
              Descargar
            </v-btn>
            <v-btn
              v-if="hasPermission('patients', 'create')"
              class="text-white border bg-primary"
              href="patients/new"
            >
              Agregar
            </v-btn>
          </template>
          <template #tabs>
            <v-tabs
              v-model="scope"
              color="primary"
            >
              <v-tab
                value="active"
                class="rounded-pill text-body-1"
              >
                {{ tabLabel('active', activePatients.length) }}
              </v-tab>
              <v-tab
                v-if="statusEnabled"
                value="inactive"
                class="rounded-pill text-body-1"
                data-testid="inactive-tab"
              >
                {{ tabLabel('inactive', inactivePatients.length) }}
              </v-tab>
              <v-tab
                value="discarded"
                class="rounded-pill text-body-1"
              >
                {{ tabLabel('discarded', (discardedPatients ?? []).length) }}
              </v-tab>
            </v-tabs>
          </template>
          <template #filters>
            <group-filter-select
              v-model="groupIdIn"
              hide-details
            />
            <service-category-filter-select
              v-model="serviceCategoryIdIn"
              v-model:uncategorized="uncategorized"
              hide-details
            />
          </template>
        </list-toolbar>
        <div
          v-if="otherTabMatches > 0 && otherKeptTab"
          class="px-4 pb-2 text-caption text-medium-emphasis"
          data-testid="other-tab-hint"
        >
          <a
            href="#"
            class="text-primary"
            @click.prevent="scope = otherKeptTab"
          >
            {{ t('patientStatus.searchHint', {
              count: otherTabMatches, tab: t(`patientStatus.tabs.${otherKeptTab}`),
            }) }}
          </a>
        </div>
        <v-snackbar
          v-model="xlsxDownloadSnackbarAlert"
          :timeout="2000"
          color="primary"
          rounded="pill"
        >
          Se está generando el archivo Excel, puede tomar unos segundos...
        </v-snackbar>
      </template>
      <template #[`item.dni`]="{ item }">
        {{ rutHelpers.rutFormat(item.dni) }}
      </template>
      <template #[`item.serviceCategoryName`]="{ item }">
        <template v-if="item.serviceCategoryName">
          {{ item.serviceCategoryName }}
          <div
            v-if="item.serviceSubcategoryName"
            class="text-caption text-medium-emphasis"
          >
            {{ item.serviceSubcategoryName }}
          </div>
        </template>
        <span
          v-else
          class="text-medium-emphasis"
        >
          Sin categoría
        </span>
      </template>
      <template #[`item.status.inactiveDays`]="{ item }">
        <v-chip
          size="small"
          variant="tonal"
          :color="statusChipColor(item.status)"
          data-testid="status-chip"
        >
          {{ statusChipLabel(item.status, t) }}
          <v-tooltip
            v-if="statusChipTooltip(item.status, t)"
            activator="parent"
            location="top"
            :text="statusChipTooltip(item.status, t)"
          />
        </v-chip>
      </template>
      <template #[`item.representative.fullName`]="{ item }">
        {{ item.representative?.fullName }}
        <div
          v-if="statusEnabled && item.representative?.phoneNumber"
          class="text-caption text-medium-emphasis"
        >
          {{ item.representative.phoneNumber }}
        </div>
      </template>
      <template #[`item.endsOn`]="{ item }">
        {{ formatShortDate(item.endsOn) || '—' }}
      </template>
      <template #[`item.discardedAt`]="{ item }">
        {{ formatShortDate(item.discardedAt) || '—' }}
      </template>
      <template #[`item.humanDiscardReason`]="{ item }">
        {{ item.humanDiscardReason || '—' }}
        <div
          v-if="item.discardNote"
          class="text-caption text-medium-emphasis"
        >
          {{ item.discardNote }}
        </div>
      </template>
      <template #[`item.actions`]="{ item }">
        <div
          v-if="scope !== 'discarded'"
          class="d-flex"
        >
          <v-btn
            v-if="hasPermission('patients', 'show')"
            icon="mdi-eye"
            size="small"
            class="me-2"
            :href="`/patients/${item.id}`"
          />
          <v-btn
            v-if="hasPermission('patients', 'update')"
            icon="mdi-pencil"
            size="small"
            class="me-2"
            :href="`/patients/${item.id}/edit`"
          />
          <v-dialog
            max-width="800"
            scrollable
          >
            <template #activator="{ props: activatorProps }">
              <v-btn
                v-if="hasPermission('patients', 'destroy')"
                v-bind="activatorProps"
                icon="mdi-archive"
                size="small"
              />
            </template>
            <template #default="{ isActive }">
              <patient-discard-form
                :patient="item"
                @close="isActive.value = false"
                @success="actionSuccess = true"
                @refetch="refetchAll"
              />
            </template>
          </v-dialog>
        </div>
        <v-dialog
          v-else
          max-width="800"
          scrollable
        >
          <template #activator="{ props: activatorProps }">
            <v-btn
              v-if="hasPermission('patients', 'update')"
              v-bind="activatorProps"
              icon="mdi-package-up"
              size="small"
            />
          </template>
          <template #default="{ isActive }">
            <patient-undiscard-form
              :patient="item"
              @close="isActive.value = false"
              @success="actionSuccess = true"
              @refetch="refetchAll"
            />
          </template>
        </v-dialog>
      </template>
      <template
        v-if="statusEnabled && scope === 'inactive'"
        #no-data
      >
        <div class="py-6 text-medium-emphasis">
          {{ t('patientStatus.inactiveEmpty') }}
        </div>
      </template>
    </v-data-table>
    <v-snackbar
      v-model="actionSuccess"
      :timeout="2000"
      color="success"
      rounded="pill"
    >
      <v-icon
        icon="mdi-check-circle"
        class="mr-4"
      />
      Cambios guardados exitosamente!
    </v-snackbar>
  </v-card>
</template>
